import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { WHEELS } from '../../src/game/data.js';
import { VehicleAssetRig } from '../../src/tools/vehicleProfile.js';

const $=(root,selector)=>root.querySelector(selector);
const $$=(root,selector)=>[...root.querySelectorAll(selector)];
const RADIUS=.92;
const AUTO_ROTATE_STORAGE_KEY='toybox-catalog-studio:wheel-auto-rotate';
const PREVIEW_VEHICLE_STORAGE_KEY='toybox-catalog-studio:wheel-preview-vehicle';

function readAutoRotatePreference(){
  try{
    const stored=window.localStorage.getItem(AUTO_ROTATE_STORAGE_KEY);
    return stored===null?true:stored==='true';
  }catch{
    return true;
  }
}

function saveAutoRotatePreference(enabled){
  try{
    window.localStorage.setItem(AUTO_ROTATE_STORAGE_KEY,String(enabled));
  }catch{
    // La vista sigue funcionando aunque el navegador bloquee localStorage.
  }
}

function readPreviewVehiclePreference(){
  try{
    return window.localStorage.getItem(PREVIEW_VEHICLE_STORAGE_KEY)||'';
  }catch{
    return '';
  }
}

function savePreviewVehiclePreference(id){
  try{
    if(id)window.localStorage.setItem(PREVIEW_VEHICLE_STORAGE_KEY,id);
    else window.localStorage.removeItem(PREVIEW_VEHICLE_STORAGE_KEY);
  }catch{
    // El selector sigue funcionando aunque el navegador bloquee localStorage.
  }
}

function cleanId(value){
  return String(value||'rueda-personalizada')
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-zA-Z0-9_-]+/g,'-')
    .replace(/^-+|-+$/g,'')
    .toLowerCase()||'rueda-personalizada';
}

function controlScale(input){
  const scale=Number(input.dataset.wheelScale);
  return Number.isFinite(scale)&&scale!==0?scale:1;
}

function randomSteppedValue(input){
  const min=Number(input.min);
  const max=Number(input.max);
  const step=Number(input.step);
  if(!Number.isFinite(min)||!Number.isFinite(max)||!Number.isFinite(step)||step<=0)return Number(input.value);
  const stepCount=Math.max(0,Math.round((max-min)/step));
  const value=min+Math.floor(Math.random()*(stepCount+1))*step;
  const decimals=Math.max(
    (String(min).split('.')[1]||'').length,
    (String(max).split('.')[1]||'').length,
    (String(step).split('.')[1]||'').length,
  );
  return Number(value.toFixed(decimals));
}

function randomHslColor(saturationRange,lightnessRange,hue=Math.random()){
  const saturation=THREE.MathUtils.lerp(saturationRange[0],saturationRange[1],Math.random());
  const lightness=THREE.MathUtils.lerp(lightnessRange[0],lightnessRange[1],Math.random());
  return `#${new THREE.Color().setHSL(hue,saturation,lightness).getHexString()}`;
}

function cloneDesign(source={}){
  return {
    color:source.color||'#d9dde2',
    accent:source.accent||'#f5f7fa',
    tireColor:source.tireColor||'#242532',
    rimStyle:source.rimStyle==='steel'?'steel':'spokes',
    tireWidth:Number(source.tireWidth??.44),
    spokes:Number(source.spokes??6),
    spokeWidth:Number(source.spokeWidth??.30),
    spokeHeight:Number(source.spokeHeight??.12),
    spokeLength:Number(source.spokeLength??.58),
    holeCount:Number(source.holeCount??8),
    holeSize:Number(source.holeSize??.085),
    holeOrbit:Number(source.holeOrbit??.38),
    steelDish:Math.min(.5,Math.max(-.5,Number(source.steelDish??.10))),
    steelWave:Math.min(.06,Math.max(0,Number(source.steelWave??.035))),
    inner:Number(source.inner??.39),
    rim:Number(source.rim??.55),
    rimThickness:Number(source.rimThickness??.055),
    rimOffset:Number(source.rimOffset??-.12),
    hubEnabled:source.hubEnabled!==false,
    hub:Number(source.hub??.28),
    hubDepth:Number(source.hubDepth??1.12),
    accentRingEnabled:source.accentRingEnabled!==false,
    accentRingRadius:Number(source.accentRingRadius??(
      source.rimStyle==='steel'?Number(source.rim??.55)*.86:.72
    )),
    accentRingThickness:Number(source.accentRingThickness??.026),
    centerCapEnabled:source.centerCapEnabled!==false,
    centerCapSize:Number(source.centerCapSize??.14),
    centerCapRelief:Number(source.centerCapRelief??.36),
    tireRoundness:Number(source.tireRoundness??.34),
    tread:Number(source.tread??0),
    metalness:Number(source.metalness??.72),
    roughness:Number(source.roughness??.30),
  };
}

function disposeObject(object){
  object?.traverse((child)=>{
    if(!child.isMesh)return;
    child.geometry?.dispose();
    const materials=Array.isArray(child.material)?child.material:[child.material];
    materials.filter(Boolean).forEach((material)=>material.dispose?.());
  });
}

function cloneObject(object){
  const clone=object.clone(true);
  clone.traverse((child)=>{
    if(!child.isMesh)return;
    child.geometry=child.geometry?.clone();
    child.material=Array.isArray(child.material)
      ?child.material.map((material)=>material.clone())
      :child.material?.clone();
  });
  return clone;
}

function sceneStats(object){
  let meshes=0;
  let triangles=0;
  object.traverse((child)=>{
    if(!child.isMesh||!child.geometry)return;
    meshes+=1;
    triangles+=child.geometry.index
      ?child.geometry.index.count/3
      :(child.geometry.attributes.position?.count||0)/3;
  });
  return {meshes,triangles:Math.round(triangles)};
}

function formatTriangles(value){
  return value>=1000?`${(value/1000).toFixed(1)}k tris`:`${value} tris`;
}

function subdivideTriangles(geometry,iterations=2){
  let current=geometry.index?geometry.toNonIndexed():geometry.clone();
  for(let iteration=0;iteration<iterations;iteration+=1){
    const source=current.getAttribute('position');
    const positions=[];
    const pushTriangle=(first,second,third)=>{
      positions.push(...first,...second,...third);
    };
    for(let index=0;index<source.count;index+=3){
      const a=[source.getX(index),source.getY(index),source.getZ(index)];
      const b=[source.getX(index+1),source.getY(index+1),source.getZ(index+1)];
      const c=[source.getX(index+2),source.getY(index+2),source.getZ(index+2)];
      const ab=a.map((value,axis)=>(value+b[axis])*.5);
      const bc=b.map((value,axis)=>(value+c[axis])*.5);
      const ca=c.map((value,axis)=>(value+a[axis])*.5);
      pushTriangle(a,ab,ca);
      pushTriangle(ab,b,bc);
      pushTriangle(ca,bc,c);
      pushTriangle(ab,bc,ca);
    }
    current.dispose();
    current=new THREE.BufferGeometry();
    current.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));
  }
  const merged=mergeVertices(current,1e-5);
  current.dispose();
  return merged;
}

function createSteelDiscGeometry(design,width){
  const holeCount=Math.max(3,Math.round(design.holeCount));
  const outerRadius=RADIUS*Math.min(.78,Math.max(
    design.rim,
    design.holeOrbit+design.holeSize+.045,
    design.hub+.10,
  ));
  const centerRadius=RADIUS*Math.max(.06,design.hub*.72);
  const orbit=RADIUS*Math.min(
    design.holeOrbit,
    (outerRadius/RADIUS)-design.holeSize-.035,
  );
  const spacingLimit=orbit*Math.sin(Math.PI/holeCount)*.72;
  const holeRadius=Math.max(.025,Math.min(
    RADIUS*design.holeSize,
    spacingLimit,
    outerRadius-orbit-RADIUS*.025,
    orbit-centerRadius-RADIUS*.025,
  ));
  const shape=new THREE.Shape();
  shape.absarc(0,0,outerRadius,0,Math.PI*2,false);
  const centerHole=new THREE.Path();
  centerHole.absarc(0,0,centerRadius,0,Math.PI*2,true);
  shape.holes.push(centerHole);
  for(let index=0;index<holeCount;index+=1){
    const angle=index/holeCount*Math.PI*2;
    const hole=new THREE.Path();
    hole.absarc(
      Math.cos(angle)*orbit,
      Math.sin(angle)*orbit,
      holeRadius,
      0,
      Math.PI*2,
      true,
    );
    shape.holes.push(hole);
  }
  let geometry=new THREE.ExtrudeGeometry(shape,{
    depth:Math.max(width*.13,.022),
    steps:1,
    curveSegments:24,
    bevelEnabled:true,
    bevelSegments:2,
    bevelSize:RADIUS*.012,
    bevelThickness:RADIUS*.012,
  });
  geometry.rotateY(Math.PI/2);
  geometry.center();
  const subdivided=subdivideTriangles(geometry,2);
  geometry.dispose();
  geometry=subdivided;
  const position=geometry.attributes.position;
  for(let index=0;index<position.count;index+=1){
    const y=position.getY(index);
    const z=position.getZ(index);
    const radial=Math.hypot(y,z);
    const normalizedRadius=Math.min(1,radial/outerRadius);
    const angle=Math.atan2(z,y);
    const dish=RADIUS*design.steelDish*Math.pow(1-normalizedRadius,1.65);
    const waveBand=Math.exp(-Math.pow((radial-orbit)/(RADIUS*.18),2));
    const wave=RADIUS*design.steelWave*Math.cos(angle*holeCount)*waveBand;
    position.setX(index,position.getX(index)+dish+wave);
  }
  geometry.computeVertexNormals();
  geometry.translate(width*.48,0,0);
  return geometry;
}

function createProceduralWheel(design){
  const width=design.tireWidth;
  const root=new THREE.Group();
  root.name='procedural_wheel_preview';
  const rubber=new THREE.MeshStandardMaterial({
    color:design.tireColor,
    roughness:.82,
    metalness:.02,
  });
  const metal=new THREE.MeshPhysicalMaterial({
    color:design.color,
    metalness:design.metalness,
    roughness:design.roughness,
    clearcoat:.42,
  });
  const accent=new THREE.MeshPhysicalMaterial({
    color:design.accent,
    metalness:Math.max(.25,design.metalness-.1),
    roughness:Math.min(.8,design.roughness+.02),
    clearcoat:.55,
  });
  const brakeMaterial=new THREE.MeshStandardMaterial({
    color:'#343943',
    metalness:.75,
    roughness:.34,
  });
  const tireProfile=[
    new THREE.Vector2(RADIUS*design.inner,-width*.5),
    new THREE.Vector2(RADIUS*.82,-width*.5),
    new THREE.Vector2(RADIUS*.97,-width*design.tireRoundness),
    new THREE.Vector2(RADIUS,width*design.tireRoundness),
    new THREE.Vector2(RADIUS*.88,width*.5),
    new THREE.Vector2(RADIUS*design.inner,width*.5),
  ];
  const tire=new THREE.Mesh(new THREE.LatheGeometry(tireProfile,32),rubber);
  tire.rotation.z=Math.PI/2;
  tire.castShadow=true;
  const brakeDisc=new THREE.Mesh(
    new THREE.CylinderGeometry(RADIUS*.55,RADIUS*.55,width*.12,32),
    brakeMaterial,
  );
  brakeDisc.rotation.z=Math.PI/2;
  const rimAssembly=new THREE.Group();
  rimAssembly.name='wheel_rim_assembly';
  rimAssembly.position.x=width*design.rimOffset;
  const rim=new THREE.Mesh(
    new THREE.TorusGeometry(RADIUS*design.rim,RADIUS*design.rimThickness,10,36),
    metal,
  );
  rim.rotation.y=Math.PI/2;
  rim.position.x=width*.54;
  root.add(tire,brakeDisc,rimAssembly);
  rimAssembly.add(rim);

  if(design.hubEnabled){
    const hub=new THREE.Mesh(
      new THREE.CylinderGeometry(
        RADIUS*design.hub,
        RADIUS*design.hub,
        width*design.hubDepth,
        24,
      ),
      accent,
    );
    hub.rotation.z=Math.PI/2;
    rimAssembly.add(hub);
  }
  if(design.accentRingEnabled){
    const accentRing=new THREE.Mesh(
      new THREE.TorusGeometry(
        RADIUS*design.accentRingRadius,
        RADIUS*design.accentRingThickness,
        8,
        36,
      ),
      accent,
    );
    accentRing.rotation.y=Math.PI/2;
    accentRing.position.x=width*.555;
    rimAssembly.add(accentRing);
  }
  if(design.centerCapEnabled){
    const centerCap=new THREE.Mesh(
      new THREE.SphereGeometry(RADIUS*design.centerCapSize,20,12),
      accent,
    );
    centerCap.scale.x=design.centerCapRelief;
    centerCap.position.x=width*.6;
    rimAssembly.add(centerCap);
  }

  if(design.rimStyle==='steel'){
    const steelDisc=new THREE.Mesh(createSteelDiscGeometry(design,width),metal);
    steelDisc.castShadow=true;
    rimAssembly.add(steelDisc);
  }else{
    for(let index=0;index<design.spokes;index+=1){
      const geometry=new RoundedBoxGeometry(
        width*design.spokeWidth,
        RADIUS*design.spokeHeight,
        RADIUS*design.spokeLength,
        2,
        RADIUS*.025,
      );
      geometry.translate(width*.53,0,-RADIUS*design.spokeLength*.5);
      geometry.rotateX(index/design.spokes*Math.PI*2);
      rimAssembly.add(new THREE.Mesh(geometry,metal));
    }
  }
  for(let index=0;index<design.tread;index+=1){
    const geometry=new RoundedBoxGeometry(width*.72,RADIUS*.13,RADIUS*.1,1,RADIUS*.018);
    geometry.translate(0,0,RADIUS*1.01);
    geometry.rotateX(index/design.tread*Math.PI*2);
    const block=new THREE.Mesh(geometry,rubber);
    block.castShadow=true;
    root.add(block);
  }
  return root;
}

export function createCatalogProceduralWheel(source={}){
  return createProceduralWheel(cloneDesign(source));
}

export class WheelEditor{
  constructor(screen,callbacks={}){
    this.screen=screen;
    this.callbacks=callbacks;
    this.canvas=$(screen,'#wheel-editor-canvas');
    this.status=$(screen,'#wheel-editor-message');
    this.saveButton=$(screen,'#wheel-editor-save');
    this.fileInput=$(screen,'#wheel-editor-file');
    this.fileName=$(screen,'#wheel-editor-file-name');
    this.fileInfo=$(screen,'#wheel-editor-file-info');
    this.autoRotateInput=$(screen,'#wheel-editor-auto-rotate');
    this.autoRotateInput.checked=readAutoRotatePreference();
    this.styleSelect=$(screen,'#wheel-editor-style');
    this.vehicleSelect=$(screen,'#wheel-editor-vehicle');
    this.vehicleControls=$(screen,'[data-wheel-vehicle-controls]');
    this.vehicleNote=$(screen,'#wheel-editor-vehicle-note');
    this.active=false;
    this.mode='procedural';
    this.previewMode='wheel';
    this.vehicleOptionsLoaded=false;
    this.vehicleLoadToken=0;
    this.mountedVehicle=null;
    this.mountedRig=null;
    this.assetFile=null;
    this.assetStats=null;
    this.design=cloneDesign(WHEELS[0]);
    this.initScene();
    this.bindInterface();
    this.syncControls();
    this.rebuildProceduralWheel();
    this.animate();
  }

  initScene(){
    this.renderer=new THREE.WebGLRenderer({
      canvas:this.canvas,
      antialias:true,
      powerPreference:'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio,1.75));
    this.renderer.outputColorSpace=THREE.SRGBColorSpace;
    this.renderer.toneMapping=THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure=1.08;
    this.renderer.shadowMap.enabled=true;

    this.scene=new THREE.Scene();
    this.scene.background=new THREE.Color('#17172a');
    this.scene.fog=new THREE.Fog('#17172a',8,18);
    this.camera=new THREE.PerspectiveCamera(38,1,.03,40);
    this.camera.position.set(4.1,2.45,4.3);
    this.controls=new OrbitControls(this.camera,this.canvas);
    this.controls.enableDamping=true;
    this.controls.dampingFactor=.075;
    this.controls.target.set(0,.12,0);
    this.controls.minDistance=2.2;
    this.controls.maxDistance=8;

    this.scene.add(new THREE.HemisphereLight(0xe8f3ff,0x25213a,2.2));
    const key=new THREE.DirectionalLight(0xffedd3,4.5);
    key.position.set(-4,7,-5);
    key.castShadow=true;
    key.shadow.mapSize.set(1024,1024);
    this.scene.add(key);
    const fill=new THREE.DirectionalLight(0x63cfff,2.2);
    fill.position.set(5,3,4);
    this.scene.add(fill);

    this.floor=new THREE.Mesh(
      new THREE.CircleGeometry(2.25,64),
      new THREE.MeshStandardMaterial({color:'#201c34',roughness:.9,metalness:.05}),
    );
    this.floor.rotation.x=-Math.PI/2;
    this.floor.position.y=-1.08;
    this.floor.receiveShadow=true;
    this.scene.add(this.floor);
    this.grid=new THREE.GridHelper(6,18,0x4dddec,0x3b3551);
    this.grid.position.y=-1.075;
    this.grid.material.transparent=true;
    this.grid.material.opacity=.28;
    this.scene.add(this.grid);
    this.previewMount=new THREE.Group();
    this.previewMount.rotation.z=-.08;
    this.scene.add(this.previewMount);

    this.loader=new GLTFLoader();
    this.loader.setMeshoptDecoder(MeshoptDecoder);
    this.dracoLoader=new DRACOLoader();
    this.dracoLoader.setDecoderPath(`${import.meta.env.BASE_URL}draco/`);
    this.loader.setDRACOLoader(this.dracoLoader);
  }

  bindInterface(){
    $(this.screen,'#wheel-editor-close').addEventListener('click',()=>this.close());
    this.saveButton.addEventListener('click',()=>this.save());
    $(this.screen,'#wheel-editor-reset').addEventListener('click',()=>this.resetDesign());
    $(this.screen,'#wheel-editor-randomize').addEventListener('click',()=>this.randomizeDesign());
    $(this.screen,'#wheel-editor-template').addEventListener('change',(event)=>{
      const template=WHEELS.find((item)=>item.id===event.target.value)||WHEELS[0];
      this.design=cloneDesign(template);
      this.syncControls();
      this.rebuildProceduralWheel();
    });
    this.styleSelect.addEventListener('change',()=>{
      const previousStyle=this.design.rimStyle;
      const nextStyle=this.styleSelect.value;
      const steelDefault=this.design.rim*.86;
      if(previousStyle!==nextStyle&&nextStyle==='steel'&&Math.abs(this.design.accentRingRadius-.72)<.001){
        this.design.accentRingRadius=steelDefault;
      }else if(previousStyle!==nextStyle&&nextStyle==='spokes'
        &&Math.abs(this.design.accentRingRadius-steelDefault)<.001){
        this.design.accentRingRadius=.72;
      }
      this.design.rimStyle=nextStyle;
      this.syncControls();
      this.rebuildProceduralWheel();
    });
    $$ (this.screen,'[data-wheel-mode]').forEach((button)=>{
      button.addEventListener('click',()=>this.setMode(button.dataset.wheelMode));
    });
    $$ (this.screen,'[data-wheel-preview-mode]').forEach((button)=>{
      button.addEventListener('click',()=>this.setPreviewMode(button.dataset.wheelPreviewMode));
    });
    this.vehicleSelect.addEventListener('change',()=>{
      savePreviewVehiclePreference(this.vehicleSelect.value);
      if(this.previewMode==='vehicle')this.loadMountedVehicle(this.vehicleSelect.value);
    });
    $$ (this.screen,'[data-wheel-design]').forEach((input)=>{
      input.addEventListener('input',()=>{
        const key=input.dataset.wheelDesign;
        this.design[key]=input.type==='checkbox'
          ?input.checked
          :input.type==='color'?input.value:Number(input.value)*controlScale(input);
        this.syncOutput(input);
        if(this.mode==='procedural')this.rebuildProceduralWheel();
      });
    });
    this.fileInput.addEventListener('change',()=>this.loadGlbFile(this.fileInput.files[0]));
    this.autoRotateInput.addEventListener('change',()=>{
      saveAutoRotatePreference(this.autoRotateInput.checked);
    });
    window.addEventListener('resize',()=>this.resize());
  }

  syncOutput(input){
    input.syncNumberControl?.();
    const output=$(this.screen,`[data-wheel-output="${input.dataset.wheelDesign}"]`);
    if(!output)return;
    const value=this.design[input.dataset.wheelDesign]/controlScale(input);
    const decimals=Number(input.step)<.01?3:2;
    output.textContent=Number.isInteger(value)?value:Number(value).toFixed(decimals);
  }

  syncControls(){
    $$ (this.screen,'[data-wheel-design]').forEach((input)=>{
      if(input.type==='checkbox')input.checked=this.design[input.dataset.wheelDesign]!==false;
      else if(input.type==='color')input.value=this.design[input.dataset.wheelDesign];
      else input.value=this.design[input.dataset.wheelDesign]/controlScale(input);
      this.syncOutput(input);
    });
    $$ (this.screen,'[data-wheel-mode]').forEach((button)=>{
      button.classList.toggle('selected',button.dataset.wheelMode===this.mode);
    });
    this.styleSelect.value=this.design.rimStyle;
    this.syncWheelStyle();
    this.syncPreviewControls();
    $(this.screen,'[data-wheel-panel="procedural"]').classList.toggle('hidden',this.mode!=='procedural');
    $(this.screen,'[data-wheel-panel="glb"]').classList.toggle('hidden',this.mode!=='glb');
  }

  syncPreviewControls(){
    $$ (this.screen,'[data-wheel-preview-mode]').forEach((button)=>{
      button.classList.toggle('selected',button.dataset.wheelPreviewMode===this.previewMode);
    });
    this.vehicleControls.classList.toggle('hidden',this.previewMode!=='vehicle');
  }

  syncWheelStyle(){
    $$ (this.screen,'[data-wheel-style-panel]').forEach((panel)=>{
      panel.classList.toggle('hidden',panel.dataset.wheelStylePanel!==this.design.rimStyle);
    });
  }

  replacePreview(object){
    if(this.previewObject){
      this.previewMount.remove(this.previewObject);
      disposeObject(this.previewObject);
    }
    this.mountedRig=null;
    this.mountedVehicle=null;
    this.previewObject=object;
    this.previewMount.add(object);
  }

  currentWheelSource(){
    if(this.mode==='procedural')return createProceduralWheel(this.design);
    if(this.mode==='glb'&&this.importedPreview)return cloneObject(this.importedPreview);
    return null;
  }

  configureStandaloneStage(){
    this.floor.position.y=-1.08;
    this.floor.scale.setScalar(1);
    this.grid.position.y=-1.075;
    this.previewMount.rotation.z=-.08;
    this.camera.position.set(4.1,2.45,4.3);
    this.controls.target.set(0,.12,0);
    this.controls.minDistance=2.2;
    this.controls.maxDistance=8;
    this.controls.update();
  }

  configureMountedStage(){
    this.floor.position.y=0;
    this.floor.scale.setScalar(1.65);
    this.grid.position.y=.005;
    this.previewMount.rotation.z=0;
    this.camera.position.set(5.7,3.15,-6.2);
    this.controls.target.set(0,.72,0);
    this.controls.minDistance=3.2;
    this.controls.maxDistance=13;
    this.controls.update();
  }

  updateMountedWheels(){
    if(!this.mountedRig)return false;
    const wheel=this.currentWheelSource();
    if(!wheel){
      this.mountedRig.wheelGroup.clear();
      disposeObject(this.mountedRig.wheelSource);
      this.mountedRig.wheelSource=null;
      this.mountedRig.wheelMounts={};
      return false;
    }
    this.mountedRig.setWheel(
      wheel,
      this.assetFile?.name||'rueda-procedural',
      {inheritBodyFinish:this.mode==='glb'},
    );
    return true;
  }

  rebuildProceduralWheel(){
    if(this.previewMode==='vehicle'){
      if(this.updateMountedWheels()){
        this.setStatus(`Vista actualizada: rueda montada en ${this.mountedVehicle?.name||'el auto seleccionado'}.`);
      }
      return;
    }
    this.configureStandaloneStage();
    this.replacePreview(createProceduralWheel(this.design));
    this.setStatus('Vista procedural actualizada. Los cambios se guardan como parámetros editables.');
  }

  async refreshVehicleOptions(){
    const preferred=this.vehicleSelect.value||readPreviewVehiclePreference();
    try{
      const vehicles=(await this.callbacks.onListVehicles?.()||[])
        .filter((item)=>item.type==='vehicle')
        .sort((first,second)=>(first.name||first.id).localeCompare(second.name||second.id,'es'));
      this.vehicleSelect.replaceChildren();
      if(!vehicles.length){
        const option=document.createElement('option');
        option.value='';
        option.textContent='No hay autos guardados';
        this.vehicleSelect.append(option);
        this.vehicleSelect.disabled=true;
        this.vehicleNote.textContent='Guardá al menos un auto calibrado para probar la rueda montada.';
        this.vehicleNote.dataset.state='warning';
        this.vehicleOptionsLoaded=true;
        return;
      }
      vehicles.forEach((item)=>{
        const option=document.createElement('option');
        option.value=item.id;
        option.textContent=item.name||item.id;
        this.vehicleSelect.append(option);
      });
      const selected=vehicles.some((item)=>item.id===preferred)?preferred:vehicles[0].id;
      this.vehicleSelect.value=selected;
      this.vehicleSelect.disabled=false;
      savePreviewVehiclePreference(selected);
      this.vehicleNote.textContent='La rueda se adapta al radio y ancho calibrados de cada eje.';
      this.vehicleNote.dataset.state='ready';
      this.vehicleOptionsLoaded=true;
      if(this.previewMode==='vehicle')await this.loadMountedVehicle(selected);
    }catch(error){
      console.error(error);
      this.vehicleSelect.replaceChildren();
      const option=document.createElement('option');
      option.value='';
      option.textContent='No se pudieron cargar los autos';
      this.vehicleSelect.append(option);
      this.vehicleSelect.disabled=true;
      this.vehicleNote.textContent=error.message||'No se pudo leer el catálogo de autos.';
      this.vehicleNote.dataset.state='warning';
      this.vehicleOptionsLoaded=true;
    }
  }

  async setPreviewMode(mode){
    if(mode!=='wheel'&&mode!=='vehicle')return;
    if(mode===this.previewMode&&mode==='vehicle'&&this.mountedRig)return;
    this.previewMode=mode;
    this.syncPreviewControls();
    if(mode==='wheel'){
      this.vehicleLoadToken+=1;
      const wheel=this.currentWheelSource();
      this.configureStandaloneStage();
      if(wheel)this.replacePreview(wheel);
      else{
        this.replacePreview(new THREE.Group());
        this.setStatus('Elegí una rueda GLB simétrica para validar y previsualizar.');
      }
      if(wheel)this.setStatus(
        this.mode==='procedural'
          ?'Vista individual de la rueda.'
          :'Vista individual de la rueda GLB.',
      );
      return;
    }
    this.configureMountedStage();
    if(!this.vehicleOptionsLoaded){
      await this.refreshVehicleOptions();
      return;
    }
    await this.loadMountedVehicle(this.vehicleSelect.value);
  }

  async loadMountedVehicle(id){
    const loadToken=++this.vehicleLoadToken;
    if(!id){
      this.replacePreview(new THREE.Group());
      this.configureMountedStage();
      this.setStatus('No hay autos disponibles para la vista de montaje.','warning');
      return;
    }
    this.vehicleSelect.disabled=true;
    this.vehicleNote.textContent='Cargando carrocería y puntos de montaje…';
    this.vehicleNote.dataset.state='ready';
    this.setStatus('Preparando la rueda montada en el auto…');
    try{
      const item=await this.callbacks.onLoadVehicle?.(id);
      if(loadToken!==this.vehicleLoadToken)return;
      if(!item?.bodyFile)throw new Error('El auto seleccionado no tiene una carrocería GLB disponible.');
      const gltf=await this.loader.parseAsync(await item.bodyFile.arrayBuffer(),'');
      if(loadToken!==this.vehicleLoadToken){
        disposeObject(gltf.scene);
        return;
      }
      const rig=new VehicleAssetRig(item.profile);
      rig.setBody(gltf.scene,item.bodyFile.name);
      const wheel=this.currentWheelSource();
      if(wheel){
        rig.setWheel(
          wheel,
          this.assetFile?.name||'rueda-procedural',
          {inheritBodyFinish:this.mode==='glb'},
        );
      }
      this.replacePreview(rig.root);
      this.mountedRig=rig;
      this.mountedVehicle=item;
      this.configureMountedStage();
      this.vehicleNote.textContent=[
        `Montada en ${item.name||item.id}: se usan las medidas calibradas de ambos ejes`,
        this.mode==='glb'?'y el acabado de pintura del auto.':'.',
      ].join(' ');
      this.vehicleNote.dataset.state='success';
      this.setStatus(
        wheel
          ?[
            `Rueda montada en ${item.name||item.id}.`,
            this.mode==='glb'
              ?'El GLB hereda el acabado configurado para la carrocería.'
              :'Podés seguir editándola en esta vista.',
          ].join(' ')
          :'Auto cargado. Importá una rueda GLB para verla en los cuatro ejes.',
        wheel?'success':'warning',
      );
    }catch(error){
      if(loadToken!==this.vehicleLoadToken)return;
      console.error(error);
      this.replacePreview(new THREE.Group());
      this.configureMountedStage();
      this.vehicleNote.textContent=error.message||'No se pudo preparar este auto.';
      this.vehicleNote.dataset.state='warning';
      this.setStatus(error.message||'No se pudo cargar el auto seleccionado.','error');
    }finally{
      if(loadToken===this.vehicleLoadToken)this.vehicleSelect.disabled=false;
    }
  }

  setMode(mode){
    if(mode!=='procedural'&&mode!=='glb')return;
    this.mode=mode;
    this.syncControls();
    this.saveButton.disabled=mode==='glb'&&!this.assetFile;
    if(mode==='procedural')this.rebuildProceduralWheel();
    else if(this.importedPreview){
      if(this.previewMode==='vehicle'){
        this.updateMountedWheels();
        this.setStatus(`Rueda GLB montada en ${this.mountedVehicle?.name||'el auto seleccionado'}.`);
      }else{
        this.configureStandaloneStage();
        this.replacePreview(cloneObject(this.importedPreview));
      }
    }else{
      if(this.previewMode==='vehicle')this.updateMountedWheels();
      this.setStatus('Elegí una rueda GLB simétrica para validar y previsualizar.');
    }
  }

  async loadGlbFile(file){
    if(!file)return;
    this.setStatus('Analizando la rueda GLB…');
    try{
      const gltf=await this.loader.parseAsync(await file.arrayBuffer(),'');
      const source=gltf.scene;
      const stats=sceneStats(source);
      const box=new THREE.Box3().setFromObject(source);
      const size=box.getSize(new THREE.Vector3());
      if(!Number.isFinite(size.length())||size.length()===0)throw new Error('El GLB no contiene geometría visible.');
      const radialSize=Math.max(size.y,size.z);
      const scale=radialSize>0?(RADIUS*2)/radialSize:1;
      const center=box.getCenter(new THREE.Vector3());
      const normalized=new THREE.Group();
      source.position.sub(center);
      source.scale.setScalar(scale);
      normalized.add(source);
      const normalizedWidth=size.x*scale;
      const widthWarning=normalizedWidth>RADIUS*1.25;
      disposeObject(this.importedPreview);
      this.importedPreview=normalized;
      this.assetFile=file;
      this.assetStats={
        ...stats,
        originalSize:{x:size.x,y:size.y,z:size.z},
        normalizedWidth,
        scale,
        widthWarning,
      };
      if(this.previewMode==='vehicle')this.updateMountedWheels();
      else{
        this.configureStandaloneStage();
        this.replacePreview(cloneObject(normalized));
      }
      this.fileName.textContent=file.name;
      this.fileInfo.textContent=`${stats.meshes} mallas · ${formatTriangles(stats.triangles)}${widthWarning?' · revisar ancho':''}`;
      this.saveButton.disabled=false;
      this.setStatus(widthWarning
        ?'GLB cargado. La profundidad parece grande para una rueda; revisá que el eje atraviese X.'
        :'GLB validado y normalizado. El eje de giro esperado atraviesa X.',
      widthWarning?'warning':'success');
    }catch(error){
      console.error(error);
      this.assetFile=null;
      this.assetStats=null;
      this.fileName.textContent='Elegir archivo GLB';
      this.fileInfo.textContent='No se pudo validar el archivo';
      this.setStatus(error.message||'No se pudo abrir la rueda GLB.','error');
    }
  }

  setStatus(message,state='ready'){
    this.status.textContent=message;
    this.status.dataset.state=state;
  }

  prepareStandaloneThumbnail(){
    if(this.previewMode!=='vehicle')return ()=>{};
    const wheel=this.currentWheelSource();
    if(!wheel)return ()=>{};
    const previousVisibility=this.previewObject?.visible;
    const floorY=this.floor.position.y;
    const floorScale=this.floor.scale.clone();
    const gridY=this.grid.position.y;
    const mountRotation=this.previewMount.rotation.clone();
    if(this.previewObject)this.previewObject.visible=false;
    this.floor.position.y=-1.08;
    this.floor.scale.setScalar(1);
    this.grid.position.y=-1.075;
    this.previewMount.rotation.z=-.08;
    this.previewMount.add(wheel);
    return ()=>{
      this.previewMount.remove(wheel);
      disposeObject(wheel);
      if(this.previewObject)this.previewObject.visible=previousVisibility;
      this.floor.position.y=floorY;
      this.floor.scale.copy(floorScale);
      this.grid.position.y=gridY;
      this.previewMount.rotation.copy(mountRotation);
    };
  }

  resetDesign(){
    const template=WHEELS.find((item)=>item.id===$(this.screen,'#wheel-editor-template').value)||WHEELS[0];
    this.design=cloneDesign(template);
    this.syncControls();
    this.rebuildProceduralWheel();
  }

  randomizeDesign(){
    const next=cloneDesign(this.design);
    next.rimStyle=Math.random()<.5?'spokes':'steel';
    $$ (this.screen,'[data-wheel-panel="procedural"] [data-wheel-design]').forEach((input)=>{
      const key=input.dataset.wheelDesign;
      if(input.type==='range'){
        next[key]=randomSteppedValue(input)*controlScale(input);
      }else if(input.type==='checkbox'){
        next[key]=Math.random()<.7;
      }
    });

    const mainHue=Math.random();
    const accentHue=(mainHue+THREE.MathUtils.lerp(.12,.82,Math.random()))%1;
    next.color=randomHslColor([.42,.92],[.34,.72],mainHue);
    next.accent=randomHslColor([.5,1],[.5,.82],accentHue);
    next.tireColor=randomHslColor([.03,.2],[.035,.16],Math.random());

    // Conserva conexiones visuales razonables aun cuando todos los controles sean aleatorios.
    next.rim=Math.min(.74,Math.max(next.rim,next.inner+.04));
    next.hub=Math.min(next.hub,Math.max(.12,next.rim-.12));
    next.accentRingRadius=Math.min(next.accentRingRadius,Math.max(.25,next.rim-.025));

    this.design=cloneDesign(next);
    this.syncControls();
    this.rebuildProceduralWheel();
    const style=this.design.rimStyle==='steel'?'chapa estampada':'rayos';
    this.setStatus(`Rueda aleatoria generada: diseño de ${style}. Todos los parámetros siguen siendo editables.`,'success');
  }

  clearWorkspace(){
    this.mode='procedural';
    this.assetFile=null;
    this.assetStats=null;
    disposeObject(this.importedPreview);
    this.importedPreview=null;
    this.fileInput.value='';
    this.fileName.textContent='Elegir archivo GLB';
    this.fileInfo.textContent='Eje de giro esperado: X';
    $(this.screen,'#wheel-editor-template').value=WHEELS[0].id;
    this.design=cloneDesign(WHEELS[0]);
    this.syncControls();
    this.rebuildProceduralWheel();
    this.saveButton.disabled=false;
  }

  open(){
    this.active=true;
    this.callbacks.onOpen?.();
    this.refreshVehicleOptions();
    requestAnimationFrame(()=>this.resize());
  }

  close(){
    this.active=false;
    this.callbacks.onClose?.();
  }

  async loadRecord(item){
    disposeObject(this.importedPreview);
    this.importedPreview=null;
    this.fileInput.value='';
    this.fileName.textContent='Elegir archivo GLB';
    this.fileInfo.textContent='Eje de giro esperado: X';
    this.mode=item.profile?.mode||'procedural';
    this.design=cloneDesign(item.profile?.design||WHEELS[0]);
    this.assetFile=item.assetFile||null;
    this.assetStats=item.profile?.asset||null;
    this.syncControls();
    if(this.mode==='glb'&&this.assetFile)await this.loadGlbFile(this.assetFile);
    else this.rebuildProceduralWheel();
    this.saveButton.disabled=this.mode==='glb'&&!this.assetFile;
  }

  async save(){
    const id=cleanId($(this.screen,'#wheel-editor-id').value);
    $(this.screen,'#wheel-editor-id').value=id;
    if(this.mode==='glb'&&!this.assetFile){
      this.setStatus('Cargá un GLB antes de guardar una rueda importada.','warning');
      return;
    }
    this.saveButton.disabled=true;
    try{
      const result=await this.callbacks.onSave?.({
        id,
        mode:this.mode,
        design:cloneDesign(this.design),
        assetFile:this.assetFile,
        assetStats:this.assetStats,
      });
      this.setStatus(result?.message||'Rueda guardada.','success');
    }catch(error){
      console.error(error);
      this.setStatus(error.message||'No se pudo guardar la rueda.','error');
    }finally{
      this.saveButton.disabled=false;
    }
  }

  resize(){
    if(!this.active)return;
    const width=this.canvas.clientWidth;
    const height=this.canvas.clientHeight;
    if(!width||!height)return;
    this.renderer.setSize(width,height,false);
    this.camera.aspect=width/height;
    this.camera.updateProjectionMatrix();
  }

  animate(){
    requestAnimationFrame(()=>this.animate());
    if(!this.active)return;
    this.resize();
    this.controls.update();
    if(this.previewMount&&this.autoRotateInput.checked)this.previewMount.rotation.y+=.0025;
    this.renderer.render(this.scene,this.camera);
  }
}
